import { task } from './helpers';
import { observable, action, computed, runInAction } from 'mobx';
import CartAPI from 'api/cart';
import { getIndex, findInArray } from 'utils/helpers';
import moment from 'moment';

export class Cart {
	@observable
	cart = {
		drop_ship: [],
		items: [],
		order_types: [],
		total_ext_price: 0,
		total_promo_price: 0,
	}; // will remain unchanged, until new cart comes from BE
	@observable cartItems = this.cart.items;
	@observable total_ext_price = 0;
	@observable total_promo_price = 0;
	@observable addedInCart = false;
	@observable cartIsUpdated = false;
	@observable itemCancelled = false;
	@observable isSuccessfullySubmited = false;
	@observable errors = [];
	@observable errorId = '';
	@observable orderTypes = [];
	@observable.ref dropshipUps = null;
	@observable selectedCandidateId = null;
	@observable validatedItem = null;

	@task
	submitOrder() {
		return CartAPI.submitOrder(moment().format('MM-DD-YYYY HH:MM'))
			.then(result => {
				if (result.is_successful) {
					this.clearCart();
					this.isSuccessfullySubmited = true;
				} else if (result.error_message === 'Submit_QuantityNotInSync') {
					this.isSuccessfullySubmited = false;
					this.addSubmitErrorMessages(result.additional_error_data);
				}
			})
			.catch(error => {
				this.isSuccessfullySubmited = false;
				return runInAction(() => {
					this.errorId = `addtocart.${error.message}`;
				});
			});
	}

	@task
	addToCart(updates) {
		const hasError = this.updateHasErrors(updates);

		if (hasError && updates[updates.length - 1].is_configurable) {
			updates.splice(0, updates.length - 1);
		}

		return CartAPI.addToCart(updates)
			.then(result => {
				this.orderTypes = result.order_types ? result.order_types : null;

				this.replaceCart(result, true);
				this.removeAPIError();
			})
			.catch(error =>
				runInAction(() => {
					this.errorId = `addtocart.${error.message}`;
				})
			);
	}

	@task
	updateCart(updates) {
		return CartAPI.updateCart(updates)
			.then(result => {
				this.orderTypes = result.order_types ? result.order_types : null;

				this.replaceCart(result, false);
				this.removeAPIError();

				runInAction(() => {
					this.cartIsUpdated = true;
				});
			})
			.catch(error =>
				runInAction(() => {
					this.errorId = `addtocart.${error.message}`;
				})
			);
	}

	@task
	validateDropShipAddress(address) {
		return CartAPI.validateDropShipAddress(address)
			.then(result => {
				runInAction(() => {
					this.dropshipUps = result;
					this.selectedCandidateId = this.dropshipUps.candidates.length
						? this.dropshipUps.candidates[0].id
						: null;

					this.removeAPIError();
				});

				this.removeAPIError();
			})
			.catch(error =>
				runInAction(() => {
					this.resetDropship();
					this.errorId = `addtocart.${error.message}`;
				})
			);
	}

	@task
	addDropShipAddress(address, item) {
		return CartAPI.addDropShipAddress(address, item)
			.then(result => {
				this.replaceCart(result, false);
				this.removeAPIError();
			})
			.catch(error =>
				runInAction(() => {
					this.resetDropship();
					this.errorId = `addtocart.${error.message}`;
				})
			);
	}

	@task
	updateDropShipAddress(address, item) {
		return CartAPI.updateDropShipAddress(address, item)
			.then(result => {
				this.replaceCart(result, false);
				this.removeAPIError();
			})
			.catch(error =>
				runInAction(() => {
					this.resetDropship();
					this.errorId = `addtocart.${error.message}`;
				})
			);
	}

	@task
	getCountriesAndStates() {
		return CartAPI.getCountriesAndStates();
	}

	@computed
	get isCartEmpty() {
		return !this.cartItems.length;
	}

	@computed
	get hasError() {
		return this.cartItems.filter(o => !!o.errorMsgId).length > 0;
	}

	@computed
	get cartLength() {
		return this.cartItems.length;
	}

	@action
	setValidatedItem(item) {
		this.validatedItem = item;
	}

	getTotalQuantity(items, id) {
		return items
			? items.reduce(
					(sum, item) => (item.id === id ? sum + item.quantity_ordered : sum),
					0
				)
			: 0;
	}

	handleState(country, stateAbbreviation) {
		this.countryUps = country === 'US' ? 'USA' : country;

		this.currentCountry = findInArray(
			this.getCountriesAndStates.state.result,
			this.countryUps,
			'value'
		);

		this.currentState = findInArray(
			this.currentCountry.states,
			stateAbbreviation,
			'abbreviation'
		).text;

		return this.currentState;
	}

	@action
	selectCandidate(id) {
		this.selectedCandidateId = id;
	}

	@action
	resetDropship() {
		this.cartItems.forEach(
			item => (item.drop_ship_address = item.initial_drop_ship_address)
		);
	}

	@action
	addSubmitErrorMessages(additionalErrorData) {
		additionalErrorData.forEach(errorItem => {
			const itemType = errorItem.is_pack ? 'pack_id' : 'id';
			const itemFromCartIndex = getIndex(
				this.cartItems,
				itemType,
				errorItem.id
			);

			if (itemFromCartIndex >= 0) {
				this.cartItems[itemFromCartIndex].quantity_available =
					errorItem.latest_available_quantity;
				this.cartItems[itemFromCartIndex].quantity =
					errorItem.latest_available_quantity;
				this.cartItems[itemFromCartIndex].errorMsgId =
					errorItem.latest_available_quantity === 0
						? 'addtocart.SubmitErrorSoldOut'
						: 'addtocart.SubmitError';
			}
		});

		this.cartItems.push(this.cartItems[this.cartItems.length - 1]);
		const lastItemindex = -1;
		this.cartItems.splice(lastItemindex, 1);
	}

	@action
	removeAPIError() {
		this.errorId = '';
	}

	@action
	replaceCart(cart, showMessage) {
		this.cart = cart;
		this.cartItems = cart.items;
		this.total_ext_price = cart.total_ext_price;
		this.total_promo_price = cart.total_promo_price;

		if (showMessage) {
			this.addedInCart = true;
		}
	}

	@action
	deleteAllUpdates() {
		this.replaceCart(this.cart, false);
	}

	@action
	clearCart() {
		this.cart = {
			drop_ship: [],
			items: [],
			order_types: [],
			total_ext_price: 0,
			total_promo_price: 0,
		};
		this.cartItems = [];
		this.total_ext_price = 0;
		this.total_promo_price = 0;
	}

	getItemsFromCart(item) {
		const itemType = item.is_pack ? 'pack_id' : 'id';
		const itemsFromCart = this.cartItems.filter(
			cartItem => cartItem[itemType] === item[itemType]
		);

		return itemsFromCart.length > 0 ? itemsFromCart : null;
	}

	getCartQuantity(item) {
		const itemsFromCart = this.getItemsFromCart(item);

		return itemsFromCart
			? itemsFromCart.reduce(
					(sum, cartElem) => sum + cartElem.quantity_ordered,
					0
				)
			: 0;
	}

	getAvailableQuantity(item, isFromCart) {
		const itemsFromCart = this.getItemsFromCart(item);

		if (itemsFromCart && !isFromCart) {
			const itemQuantity = itemsFromCart ? itemsFromCart[0].quantity : 0;
			const itemQuantityOrdered = this.getTotalQuantityFromCart(item);

			return itemQuantity - itemQuantityOrdered;
		}

		return item.quantity;
	}

	getTotalQuantityFromCart(item) {
		const itemsFromCart = this.getItemsFromCart(item);
		return itemsFromCart
			? itemsFromCart.reduce(
					(sum, cartElem) => sum + cartElem.quantity_ordered,
					0
				)
			: 0;
	}

	updateHasErrors(updates) {
		let hasError = false;
		updates.some(element => {
			if (element.errorMsgId) {
				hasError = true;
				return true;
			}

			return false;
		});

		return hasError;
	}

	@computed
	get hasMultipleEntryDropships() {
		return this.dropshipUps.candidates.length > 1;
	}

	@computed
	get hasOneEntryDropship() {
		return this.dropshipUps.candidates.length === 1;
	}

	@computed
	get selectCandidateId() {
		return this.dropshipUps.candidates.find(
			c => c.id === this.selectedCandidateId
		);
	}
}

export default new Cart();



// WEBPACK FOOTER //
// ./src/state/Cart.js